import React, { forwardRef, useMemo } from 'react';

import { classNames } from '@silo/ui';
import { escapeRegExp } from 'app/functions/format';

const defaultClassName = 'w-0 p-0 text-2xl bg-transparent';

type NumericalInputProps = {
  value: string | number;
  onUserInput: (input: string) => void;
  error?: boolean;
  fontSize?: string;
  align?: 'right' | 'left';
  decimals: number;
} & Omit<React.HTMLProps<HTMLInputElement>, 'onChange' | 'as'>;

export const NumericalInput = forwardRef<HTMLInputElement, NumericalInputProps>(
  ({ value, onUserInput, placeholder, className = defaultClassName, decimals, ...rest }, ref) => {
    // match escaped "." characters via in a non-capturing group
    const inputRegex = useMemo(() => RegExp(`^\\d*(?:\\\\[.])?\\d{0,${decimals}}$`), [decimals]);

    const enforcer = (nextUserInput: string) => {
      if (nextUserInput === '' || inputRegex.test(escapeRegExp(nextUserInput))) {
        onUserInput(nextUserInput);
      }
    };

    return (
      <input
        {...rest}
        value={value}
        onChange={(event) => {
          // replace commas with periods, because uniswap exclusively uses period as the decimal separator
          enforcer(event.target.value.replace(/,/g, '.'));
        }}
        // universal input options
        inputMode="decimal"
        title="Token Amount"
        autoComplete="off"
        autoCorrect="off"
        // text-specific options
        type="text"
        pattern="^[0-9]*[.,]?[0-9]*$"
        placeholder={placeholder || '0.00'}
        min={0}
        minLength={1}
        maxLength={79}
        spellCheck="false"
        className={classNames(
          'relative outline-none border-none flex-auto overflow-hidden overflow-ellipsis placeholder:text-white',
          className
        )}
        ref={ref}
      />
    );
  }
);

NumericalInput.displayName = 'NumericalInput';

// const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`) // match escaped "." characters via in a non-capturing group
